import React from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, Sprout, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useRegion, isIndia } from "../contexts/RegionContext";
import { setProgrammeIntent } from "../lib/nav";
import { PRICING_TIERS } from "../data/content";

const Price = ({ tier }) => {
    const { region } = useRegion();
    const inIndia = isIndia(region);
    const data = inIndia ? tier.india : tier.global;
    const symbol = inIndia ? "₹" : "$";
    return (
        <div className="flex items-baseline gap-2" data-testid={`price-${tier.id}`}>
            <span className="font-display text-5xl text-ivory tracking-tight">
                {symbol}{data.amount}
            </span>
            <span className="text-xs uppercase tracking-widest text-ivory/40 font-mono">{data.suffix}</span>
        </div>
    );
};

const Pricing = () => {
    const { region } = useRegion();
    const inIndia = isIndia(region);

    return (
        <section id="pricing" className="relative py-24 lg:py-32" data-testid="pricing">
            <div className="max-w-7xl mx-auto px-5 lg:px-10">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
                    <div>
                        <div className="pill"><span className="accent-dot" />Transparent pricing</div>
                        <h2 className="mt-5 font-display text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">
                            Urban-IT quality.<br />
                            <span className="italic text-gold-shimmer">Sensible</span> economics.
                        </h2>
                    </div>
                    <div className="text-ivory/60 max-w-md lg:text-right">
                        <p>
                            Showing <span className="text-gold font-medium">{region.name} · {region.currency}</span>
                            {inIndia && <span className="text-teal"> · India rebate applied</span>}.
                        </p>
                        <p className="text-sm mt-2 text-ivory/40">Switch region in the top-right at any time.</p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-3 gap-5">
                    {PRICING_TIERS.map((tier, i) => (
                        <motion.div
                            key={tier.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                            data-testid={`tier-${tier.id}`}
                        >
                            {tier.highlight && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                                    <span className="gold-pulse inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-obsidian text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">
                                        <Sparkles className="w-3 h-3" />
                                        Most popular
                                    </span>
                                </div>
                            )}

                            <div
                                className={`relative rounded-2xl p-8 lg:p-10 ${tier.highlight
                                    ? "foil-sheen foil-border bg-gradient-to-b from-[#1A1408] via-[#0F0F14] to-obsidian shadow-[0_40px_100px_-30px_rgba(212,168,87,0.45)]"
                                    : "glass glass-hover"
                                }`}
                            >
                                <div className={`${tier.highlight ? "relative z-[1]" : ""}`}>
                                    <div className="flex items-baseline justify-between">
                                        <h3 className={`font-display text-3xl ${tier.highlight ? "text-gold-shimmer" : "text-ivory"}`}>{tier.name}</h3>
                                        <span className="font-mono text-[10px] uppercase tracking-widest text-ivory/40">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <p className="mt-2 text-ivory/55 text-sm">{tier.tagline}</p>

                                    <div className="mt-7"><Price tier={tier} /></div>

                                    <ul className="mt-8 space-y-3">
                                        {tier.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2.5 text-sm text-ivory/80">
                                                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.highlight ? "text-gold" : "text-teal"}`} />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        to="/contact"
                                        className={`mt-8 w-full ${tier.highlight ? "btn-gold" : "btn-ghost"} block text-center`}
                                        data-testid={`tier-cta-${tier.id}`}
                                    >
                                        {tier.ctaLearn}
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Rural / NGO / First-cohort Programme */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-14 relative rounded-2xl overflow-hidden"
                    style={{
                        background:
                            "linear-gradient(120deg, rgba(45,212,191,0.08) 0%, rgba(212,168,87,0.08) 60%, rgba(20,20,26,0.6) 100%)",
                        border: "1px solid rgba(45, 212, 191, 0.25)",
                        boxShadow: "0 30px 80px -40px rgba(45,212,191,0.3)",
                    }}
                    data-testid="programme-banner"
                >
                    {/* Decorative leaf glow */}
                    <div
                        className="absolute -right-20 -top-20 w-80 h-80 opacity-20 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(45,212,191,0.5), transparent 60%)",
                        }}
                    />

                    <div className="relative grid lg:grid-cols-12 gap-8 p-8 lg:p-12 items-center">
                        <div className="lg:col-span-8">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal/30 bg-teal/10 text-teal text-[10px] font-mono uppercase tracking-widest">
                                <Sprout className="w-3 h-3" />
                                Rural · NGO · First-cohort programme
                            </div>
                            <h3 className="mt-5 font-display text-4xl lg:text-5xl text-ivory tracking-tight leading-[1.05]">
                                Rural-built. <span className="italic text-gold-shimmer">So we mean it.</span>
                            </h3>
                            <p className="mt-4 text-ivory/70 leading-relaxed max-w-2xl">
                                Verified rural enterprises, NGOs, foundations and educational institutions qualify for an additional <span className="text-gold font-medium">25–40% off</span> our India rates.
                                One short qualifying conversation with the founder. No paperwork theatre, no committees.
                            </p>

                            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl">
                                {[
                                    "Verified rural / tier-3 organisations",
                                    "Registered NGOs & foundations",
                                    "Educational institutions",
                                    "First-cohort community partners",
                                ].map((eligible) => (
                                    <li key={eligible} className="flex items-start gap-2 text-sm text-ivory/75">
                                        <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                                        <span>{eligible}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-4">
                            <div className="text-left lg:text-right">
                                <div className="font-display text-5xl lg:text-6xl text-gold-shimmer leading-none">25–40%</div>
                                <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ivory/50">off India rates</div>
                            </div>
                            <Link
                                to="/contact"
                                onClick={setProgrammeIntent}
                                className="btn-gold !py-3 !px-5"
                                data-testid="programme-cta"
                            >
                                Apply for programme rates
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <p className="text-[11px] text-ivory/40 max-w-xs lg:text-right">
                                One conversation. Decisions in 48 hours. Founder reviews every application personally.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Pricing;
